import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient, useQueries } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useAuth } from '../../../global/contexts/auth/auth';
import { isDiagnosable, dxPhaseInfo, daysBetween, DX_PHASES } from '../../../core/constants/dx';
import { sessionColor } from '../../../core/constants/transfers';
import { useOpuTransferSessions } from '../../transfers/contexts/transfers';
import { transferKeys } from '../../transfers/queries/keys';
import { transferPaths } from '../../transfers/queries/paths';
import SessionLegend from '../../transfers/layouts/session-legend';
import CollapsibleGroup from '../../../common/components/CollapsibleGroup/CollapsibleGroup';
import { IconPrinter } from '../../../common/icons';
import { useGeneratePdf } from '../../reports/contexts/reports';
import { downloadPdf } from '../../reports/utils/shell';
import { pregnancyKeys } from '../queries/keys';
import { pregnancyPaths } from '../queries/paths';
import { buildDxPhaseReport } from '../utils/report';
import DxRows from '../forms/dx-rows';

import './styles/dx.css';

const today = () => new Date().toLocaleDateString('en-CA');

// Agrupa las filas del worklist por LOTE (finca · sesión TE). La OPU es la misma para toda la vista
// (?opu=), así que va en la cabecera como contexto. Las fechas nominales vienen de la sesión.
function groupRowsByBatch(rows, opuCode) {
    const groups = new Map();
    for (const r of rows) {
        const key = `${r.recipient_farm?.id ?? 'x'}|${r.sessionId ?? 'x'}`;
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                farm: r.recipient_farm?.name || null,
                opu: opuCode || null,
                te: r.sessionCode || null,
                dates: r.sessionDates || {},
                items: [],
            });
        }
        groups.get(key).items.push(r);
    }
    return [...groups.values()];
}

// Resumen de Dx (por estado de la preñez = resultado ya reportado), sobre la finca
// en vista. `open` = pendiente (sin resultado terminal; incluye doubtful/ppl→ya no).
const DX_KPIS = [
    { key: 'total',     label: 'Preñeces',   tone: '' },
    { key: 'open',      label: 'Pendientes', tone: 'open' },
    { key: 'confirmed', label: 'Preñadas',   tone: 'good' },
    { key: 'lost',      label: 'Vacías',     tone: 'bad' },
];

// Confirmación de dx extemporáneo. Componente propio para que useModalDialog monte
// junto con el alertdialog (foco inicial, Escape, trampa de Tab, retorno de foco).
function ExtempWarn({ days, phase, onCancel, onConfirm }) {
    const cardRef = useModalDialog(onCancel);
    return (
        <div className="dx-warn" role="alertdialog" aria-modal="true" aria-label="Diagnóstico extemporáneo">
            <div className="dx-warn__overlay" onClick={onCancel} aria-hidden="true" />
            <div className="dx-warn__card" ref={cardRef}>
                <h2 className="dx-warn__title">Diagnóstico extemporáneo</h2>
                <p className="dx-warn__text">
                    Faltan <strong>{days} día(s)</strong> para la fecha estimada de <strong>dx{phase}</strong>.
                    Estás registrando la palpación antes de tiempo. ¿Seguro deseás continuar?
                </p>
                <div className="dx-warn__actions">
                    <button type="button" className="dx-warn__cancel" onClick={onCancel}>Cancelar</button>
                    <button type="button" className="dx-warn__confirm" onClick={onConfirm}>Continuar</button>
                </div>
            </div>
        </div>
    );
}

// Diagnóstico de preñez ANCLADO A UNA OPU (?opu=), DIVIDIDO EN FASES (las más comunes):
// DX1 y DX2. Normalmente se hacen el mismo día en transacciones distintas: primero se
// reportan los dx1; cuando ya no quedan, la vista abre dx2. Por fase se registra (POST)
// el dx que toca (next_dx) o se EDITA (PATCH, solo el último, 0031) el ya registrado;
// nunca dx2 sin dx1. Si se registra antes de la fecha estimada → aviso de extemporaneidad.
// Al guardar se redirige a la OPU (KPIs + informe con fechas estimadas siguientes).
export default function DxWorkspace() {
    const navigate = useNavigate();
    const qc = useQueryClient();
    const { can } = useAuth();
    const canWrite = can('pregnancies', 'write');
    const [params] = useSearchParams();
    const opuId = params.get('opu');

    const [farm, setFarm] = useState('');
    const [date, setDate] = useState(today());
    const [results, setResults] = useState({});
    const [saving, setSaving] = useState(false);
    const [phase, setPhase] = useState(null); // null = auto (dx1 si quedan dx1 pendientes, si no dx2)

    // Impresión en MODO SELECCIÓN: las casillas no existen hasta que se pulsa imprimir,
    // así la cabecera de cada lote no carga un control extra en el uso normal. Se guardan
    // los lotes EXCLUIDOS (no los incluidos) para que al entrar al modo —y ante cualquier
    // lote nuevo— todo arranque seleccionado.
    const { mutateAsync: generatePdf } = useGeneratePdf();
    const [selectMode, setSelectMode] = useState(false);
    const [excluded, setExcluded] = useState(() => new Set());
    const [printing, setPrinting] = useState(false);
    const toggleGroup = (key, on) => setExcluded((prev) => {
        const next = new Set(prev);
        if (on) next.delete(key); else next.add(key);
        return next;
    });
    const openSelect = () => { setExcluded(new Set()); setSelectMode(true); };
    const closeSelect = () => { setSelectMode(false); setExcluded(new Set()); };

    const { data: opuData, isLoading, isFetching, error } = useOpuTransferSessions(opuId, { enabled: !!opuId });

    // Enriquecimiento capa 1: ensamblado de cada sesión TE (0027) → genealogía
    // (donadora×toro) + cuerpo lúteo (CL). Map por transferId.
    const sessionIds = (opuData?.transfer_sessions || []).map((s) => s.id);
    const detailQueries = useQueries({
        queries: sessionIds.map((sid) => ({
            queryKey: transferKeys.detail(sid),
            queryFn: ({ signal }) => api.get(transferPaths.session(sid), { signal }).then((r) => r.data),
            enabled: !!sid,
        })),
    });
    const detailsReady = detailQueries.map((q) => q.dataUpdatedAt).join(',');
    const byTransferId = useMemo(() => {
        const m = new Map();
        detailQueries.forEach((q) => (q.data?.transfers || []).forEach((t) => m.set(t.id, t)));
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailsReady]);

    // OPU → sesiones TE → transfers → TODAS las preñeces (respeta el índice; orden
    // estable por id de embrión; color por sesión). Solo open/confirmed son diagnosticables.
    const all = useMemo(() => {
        const out = [];
        (opuData?.transfer_sessions || []).forEach((s, si) => {
            const color = sessionColor(si);
            (s.transfers || []).forEach((t) => {
                if (!t.pregnancy?.id) return;
                const full = byTransferId.get(t.id);
                out.push({
                    id: t.pregnancy.id,
                    state: t.pregnancy.state,
                    recipient: t.recipient,
                    embryo: full?.embryo || t.embryo,
                    cl: full?.remarks?.corpusLuteum || null,
                    recipient_farm: s.farm,
                    sessionId: s.id,
                    sessionCode: s.code,
                    // Fechas nominales del lote (las calcula el backend desde la fecha de la sesión TE).
                    sessionDates: { te: s.date, dx1: s.dx1_expected, dx2: s.dx2_expected },
                    color,
                    diagnosable: isDiagnosable(t.pregnancy.state),
                });
            });
        });
        return out.sort((a, b) => (a.embryo?.id || 0) - (b.embryo?.id || 0));
    }, [opuData, byTransferId]);

    const farmOptions = useMemo(() => {
        const m = new Map();
        all.forEach((p) => { if (p.recipient_farm?.id != null) m.set(p.recipient_farm.id, p.recipient_farm.name); });
        return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [all]);

    // Vista filtrada por finca (rondas).
    const base = useMemo(() => (farm ? all.filter((p) => String(p.recipient_farm?.id) === farm) : all), [all, farm]);

    // Enriquecimiento capa 2: detalle de cada preñez → diagnostics[] (historial) + next_dx (0030/0031).
    // Acotado a la finca en vista. Incluye las `lost`: una vaca marcada VACÍA deja de ser diagnosticable,
    // pero necesitamos su dx para que siga visible en su fase (read-back), no que desaparezca del worklist.
    const pregIds = base.filter((p) => p.diagnosable || p.state === 'lost').map((p) => p.id);
    const pregQueries = useQueries({
        queries: pregIds.map((pid) => ({
            queryKey: pregnancyKeys.detail(pid),
            queryFn: ({ signal }) => api.get(pregnancyPaths.detail(pid), { signal }).then((r) => r.data),
            enabled: !!pid,
        })),
    });
    const pregReady = pregQueries.map((q) => q.dataUpdatedAt).join(',');
    const byPregnancyId = useMemo(() => {
        const m = new Map();
        pregQueries.forEach((q) => { if (q.data?.id != null) m.set(q.data.id, q.data); });
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pregReady]);
    const enriching = detailQueries.some((q) => q.isLoading || q.isFetching)
        || pregQueries.some((q) => q.isLoading || q.isFetching);

    const rows = useMemo(() => base.map((p) => {
        const det = byPregnancyId.get(p.id);
        return {
            ...p,
            diagnostics: det?.diagnostics || [],
            nextDx: det?.next_dx ?? null,
            // `forecast` trae parto_estimated — lo usa el PDF de la fase (columna "Parto prob.",
            // paridad con el informe reproductivo). Ya venía en el detalle; solo faltaba propagarlo.
            forecast: det?.forecast ?? null,
            detailLoaded: !!det,
        };
    }), [base, byPregnancyId]);

    // Fase activa: explícita o auto (dx1 mientras queden dx1 por registrar; si no, dx2).
    const dx1Pending = rows.some((r) => r.detailLoaded && dxPhaseInfo(r.diagnostics, r.nextDx, 1).mode === 'register');
    const activePhase = phase ?? (dx1Pending ? 1 : 2);

    // Filas de la fase activa: clasificadas por dxPhaseInfo. Se ocultan las que no
    // aplican (na) y las que requieren dx1 (needs-prev) → no se permite dx2 sin dx1.
    // Entran las diagnosticables (open/confirmed) Y las `lost` que YA tienen un dx registrado
    // EN ESTA FASE: así una vaca recién marcada VACÍA sigue visible con su resultado (igual que
    // una preñada se queda tras confirmarse), en vez de desaparecer y parecer que no se guardó.
    // Una `lost` sin detalle aún no entra (no sabemos si su dx es de esta fase → evita "Cargando"
    // fantasma). Las diagnosticables sin detalle sí se muestran provisionales ("Cargando…").
    const phaseRows = useMemo(() => rows.filter((r) => r.diagnosable
        || (r.detailLoaded && (r.diagnostics || []).some((d) => d.number === activePhase && d.result != null)))
        .map((r) => {
            const info = dxPhaseInfo(r.diagnostics, r.nextDx, activePhase);
            const earlyDays = info.mode === 'register' && info.scheduledFor ? daysBetween(date, info.scheduledFor) : null;
            return { ...r, info: { ...info, earlyDays } };
        }).filter((r) => !r.detailLoaded || (r.info.mode !== 'na' && r.info.mode !== 'needs-prev')), [rows, activePhase, date]);

    // Resumen por estado (sobre la finca en vista, independiente de la fase).
    const summary = useMemo(() => {
        const s = { total: base.length, open: 0, confirmed: 0, lost: 0, born: 0 };
        base.forEach((p) => { if (s[p.state] != null) s[p.state] += 1; });
        return s;
    }, [base]);

    const [extemp, setExtemp] = useState(null); // aviso de dx extemporáneo: { id, result, days }
    // cada fase = una transacción; al cambiarla los lotes se rearman, así que se sale del
    // modo selección para no arrastrar una selección hecha sobre los lotes de la otra fase.
    const changePhase = (n) => { setPhase(n); setResults({}); closeSelect(); };
    const setResult = (id, result) => setResults((prev) => {
        const next = { ...prev };
        if (result == null) delete next[id]; else next[id] = result;
        return next;
    });
    // Al INGRESAR un resultado en una fila extemporánea (antes de la fecha estimada de
    // dxN), se abre el modal de advertencia; al confirmar se aplica la marca, al
    // cancelar se descarta. Limpiar la marca (result null) nunca advierte.
    const handleResult = (id, result) => {
        if (result == null) { setResult(id, null); return; }
        const row = phaseRows.find((r) => r.id === id);
        const days = row?.info?.earlyDays;
        if (days > 0) { setExtemp({ id, result, days }); return; }
        setResult(id, result);
    };
    const confirmExtemp = () => { if (extemp) setResult(extemp.id, extemp.result); setExtemp(null); };

    // Acciones pendientes de la fase: register con pick, o edit con pick distinto al actual.
    const pending = phaseRows.filter((r) => {
        const pick = results[r.id];
        if (pick == null) return false;
        if (r.info.mode === 'register') return true;
        if (r.info.mode === 'edit') return pick !== r.info.current;
        return false;
    });
    const pendingCount = pending.length;

    // Lotes (finca · sesión TE) de la fase activa — se usan para render y para imprimir.
    const groups = useMemo(
        () => groupRowsByBatch(phaseRows, opuData?.opu?.code),
        [phaseRows, opuData],
    );
    const printGroups = groups.filter((g) => !excluded.has(g.key));
    const printCount = printGroups.reduce((n, g) => n + g.items.length, 0);
    // Marcas sin guardar dentro del alcance elegido: NO salen en el PDF (solo se avisa).
    const pendingIds = new Set(pending.map((r) => r.id));
    const unsavedInScope = printGroups.reduce(
        (n, g) => n + g.items.filter((r) => pendingIds.has(r.id)).length, 0);

    const handlePrint = async () => {
        if (!printGroups.length) { toast.error('Seleccioná al menos un lote para imprimir'); return; }
        setPrinting(true);
        try {
            const { html, filename, options } = buildDxPhaseReport(printGroups, {
                phase: activePhase,
                opuCode: opuData?.opu?.code,
                scopeLabel: farm
                    ? farmOptions.find((f) => String(f.id) === farm)?.name
                    : 'Todas las fincas',
                journeyDate: date,
            });
            const blob = await generatePdf({ html, filename, options });
            downloadPdf(blob, filename);
            toast.success(`Informe de dx${activePhase} generado`);
            closeSelect();
        } catch (err) {
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo generar el informe');
        } finally {
            setPrinting(false);
        }
    };

    const handleSave = async () => {
        if (!pending.length) { toast.error('Marcá al menos un diagnóstico'); return; }
        if (!date) { toast.error('Indicá la fecha de la jornada'); return; }
        // La extemporaneidad ya se confirmó al ingresar cada marca (modal por fila).

        setPhase(activePhase); // fija la fase: no auto-flip mientras se guarda / si queda en parcial
        setSaving(true);
        const failures = [];
        const succeeded = [];
        let attempted = 0;
        const run = async (id, label, fn) => { attempted++; try { await fn(); succeeded.push(id); } catch (err) { failures.push(err?.message ? `${label} (${err.message})` : label); } };

        for (const r of pending) {
            const pick = results[r.id];
            const label = `receptora ${r.recipient?.number || `#${r.id}`}`;
            if (r.info.mode === 'edit') {
                await run(r.id, label, () => api.patch(pregnancyPaths.diagnosticItem(r.id, r.info.dxId), { date, result: pick }));
            } else {
                await run(r.id, label, () => api.post(pregnancyPaths.diagnostics(r.id), { date, result: pick }));
            }
        }

        setSaving(false);
        qc.invalidateQueries({ queryKey: pregnancyKeys.all });
        if (opuId) qc.invalidateQueries({ queryKey: ['transfer-sessions', 'opu', opuId] });
        qc.invalidateQueries({ queryKey: ['opu'] });
        qc.invalidateQueries({ queryKey: ['kpis'] });
        // Limpio solo las marcas guardadas con éxito → las fallidas quedan para reintentar.
        if (succeeded.length) setResults((prev) => { const n = { ...prev }; succeeded.forEach((id) => delete n[id]); return n; });
        if (attempted > 0 && failures.length === attempted) { toast.error(`No se pudo guardar: ${failures.join(' · ')}`); return; }
        if (failures.length) { toast.error(`Guardado con avisos: ${failures.join(' · ')}`); return; }
        toast.success(`${attempted} diagnóstico(s) de dx${activePhase} registrados`);
        // Al finalizar: a la OPU (KPIs + fases + informe con fechas estimadas siguientes).
        navigate(`/admin/opu/${opuId}`);
    };

    if (!opuId) {
        return <div className="dx"><div className="dx__state">Abrí el diagnóstico desde una sesión OPU (paso “Dx” del avance).</div></div>;
    }

    return (
        <div className="dx">
            <header className="dx__header">
                <div className="dx__heading">
                    <h1 className="dx__title">Diagnósticos</h1>
                    {opuData?.opu?.code && <span className="dx__opu">{opuData.opu.code}</span>}
                    <span className="dx__count">{phaseRows.length}</span>
                </div>
                <div className="dx__phases" role="tablist" aria-label="Fase de diagnóstico">
                    {DX_PHASES.map((p) => (
                        <button
                            key={p.value}
                            type="button"
                            role="tab"
                            aria-selected={activePhase === p.value}
                            className={`dx__phase ${activePhase === p.value ? 'dx__phase--active' : ''}`}
                            onClick={() => changePhase(p.value)}
                            disabled={saving}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </header>

            <div className="dx__toolbar">
                <label className="dx__field">
                    <span className="dx__label">Finca</span>
                    {/* Las marcas NO se borran al cambiar de finca (rondas); sí al cambiar de fase. */}
                    <select value={farm} onChange={(e) => setFarm(e.target.value)} disabled={saving}>
                        <option value="">Todas las fincas</option>
                        {farmOptions.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                </label>

                <label className="dx__field">
                    <span className="dx__label">Fecha de la jornada</span>
                    <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} disabled={saving} />
                </label>

                {pendingCount > 0 && (
                    <span className="dx__pending" title="Diagnósticos marcados sin guardar (se mantienen al cambiar de finca)">
                        {pendingCount} marcada(s) sin guardar
                    </span>
                )}

                {/* Solo abre/cierra el modo selección: las casillas de los lotes aparecen ahí.
                    Icon-only, con el alcance en el tooltip / aria-label. */}
                <button
                    type="button"
                    className={`dx__print ${selectMode ? 'dx__print--active' : ''}`}
                    onClick={selectMode ? closeSelect : openSelect}
                    disabled={!groups.length}
                    aria-pressed={selectMode}
                    aria-label={selectMode ? 'Salir del modo de selección' : `Imprimir dx${activePhase}: elegir lotes`}
                    title={selectMode ? 'Salir del modo de selección' : `Imprimir dx${activePhase} · elegir qué lotes incluir`}
                >
                    <IconPrinter />
                </button>

                {canWrite && (
                    <button type="button" className="dx__save" onClick={handleSave} disabled={saving || !pendingCount}>
                        {saving ? 'Guardando…' : `Guardar dx${activePhase}${pendingCount ? ` (${pendingCount})` : ''}`}
                    </button>
                )}
            </div>

            {selectMode && (
                <div className="dx-select" role="region" aria-label="Selección de lotes para imprimir">
                    <div className="dx-select__info">
                        <span className="dx-select__count">
                            {printGroups.length} de {groups.length} lote(s) · {printCount} receptora(s)
                        </span>
                        <span className="dx-select__hint">Marcá en cada cabecera los lotes a incluir.</span>
                        {unsavedInScope > 0 && (
                            <span className="dx-select__warn">
                                {unsavedInScope} marca(s) sin guardar no saldrán en el PDF — guardá primero si querés incluirlas.
                            </span>
                        )}
                    </div>
                    <div className="dx-select__actions">
                        <button type="button" className="dx-select__cancel" onClick={closeSelect} disabled={printing}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="dx-select__confirm"
                            onClick={handlePrint}
                            disabled={printing || !printGroups.length}
                        >
                            {printing ? 'Generando…' : 'Generar PDF'}
                        </button>
                    </div>
                </div>
            )}

            {error && <div className="dx__error">{error.message || 'No se pudieron cargar las transferencias de la OPU'}</div>}

            {!isLoading && base.length > 0 && (
                <section className="dx-kpis" aria-label="Resumen de diagnósticos">
                    {DX_KPIS.map((k) => (
                        <div key={k.key} className={`dx-kpi ${k.tone ? `dx-kpi--${k.tone}` : ''}`}>
                            <span className="dx-kpi__value">{summary[k.key]}</span>
                            <span className="dx-kpi__label">{k.label}</span>
                        </div>
                    ))}
                </section>
            )}

            {!isLoading && base.length > 0 && <SessionLegend sessions={opuData?.transfer_sessions} />}

            {isLoading ? (
                <div className="dx__state">Cargando transferencias…</div>
            ) : !base.length ? (
                <div className="dx__state">{farm ? 'No hay transferencias en esa finca.' : 'Esta OPU no tiene transferencias.'}</div>
            ) : !phaseRows.length ? (
                <div className="dx__state">No hay preñeces para dx{activePhase}{activePhase === 2 ? ' (requieren dx1 primero).' : '.'}</div>
            ) : (
                <div className={`dx__card ${isFetching || enriching ? 'dx__card--busy' : ''}`}>
                    {/* Lotes plegables por (finca · sesión TE): la cabecera resume el lote y sus fechas
                        nominales (TE / dx1 / dx2), que todas sus receptoras comparten. Al desplegar, la
                        misma grilla de siempre. */}
                    {/* Sin `badge` ni `opu` A PROPÓSITO: la fase ya está en las pestañas y la OPU
                        en el título, así que ambos se repetían idénticos en cada lote sin
                        distinguir nada. Se omiten solo acá — otras pantallas que usan
                        CollapsibleGroup (p.ej. la agenda) los siguen pasando y no cambian. */}
                    {groups.map((g) => (
                        <CollapsibleGroup
                            key={g.key}
                            farm={g.farm}
                            te={g.te}
                            dates={g.dates}
                            count={g.items.length}
                            defaultOpen
                            selectable={selectMode}
                            selected={!excluded.has(g.key)}
                            onSelect={(on) => toggleGroup(g.key, on)}
                            selectTitle={`Incluir ${g.farm || 'este lote'}${g.te ? ` · ${g.te}` : ''} en el PDF`}
                        >
                            <DxRows rows={g.items} phase={activePhase} results={results} onResult={handleResult} canWrite={canWrite} saving={saving} />
                        </CollapsibleGroup>
                    ))}
                </div>
            )}

            {extemp && (
                <ExtempWarn days={extemp.days} phase={activePhase} onCancel={() => setExtemp(null)} onConfirm={confirmExtemp} />
            )}
        </div>
    );
}
